import useCallDirectionFlags from '@src/hooks/call/useCallDirectionFlags';
import CallMediaSingleton from '@src/logic/call/callMedia/CallMediaSingleton';
import useHandleCallMediaError from '@src/logic/call/callMedia/hooks/useHandleCallMediaError';
import generateRoomId from '@src/logic/call/generateRoomId';
import {
  answeringFinished,
  answeringStarted,
  endedCall,
} from '@src/reducers/calls';
import {
  StartCallRequest,
  useRequestEndCallMutation,
  useRequestStartCallMutation,
} from '@src/reducers/callsAPI';
import {
  selectAnsweringCallId,
  selectCanAnswerCall,
  selectIsCallEnded,
} from '@src/selectors/calls';
import { selectAuthenticationInfo } from '@src/selectors/user';
import type { RootState } from '@src/store/reduxstore';
import { Call, CallAgent, CallUser } from '@src/types/calls';
import { useState } from 'react';
import { useDispatch, useSelector, useStore } from 'react-redux';

const useCallMedia = (call: Call) => {
  const dispatch = useDispatch();
  const store = useStore<RootState>();
  const agent = useSelector(selectAuthenticationInfo);
  const answeringCallId = useSelector(selectAnsweringCallId);
  const callDirection = useCallDirectionFlags(call.direction);
  const handleCallMediaError = useHandleCallMediaError();

  const [isMuted, setIsMuted] = useState(false);
  const [isStarting, setIsStarting] = useState(false);
  const [isEnding, setIsEnding] = useState(false);

  const [requestStartCallMutation, { isLoading: isStartingMutation }] =
    useRequestStartCallMutation();
  const [requestEndCallMutation, { isLoading: isEndingMutation }] =
    useRequestEndCallMutation();

  // Shared through the store: the chat list and the agent console each mount this hook
  // for the same call, and an answer started from one must show (and block) on both.
  const starting =
    isStarting || isStartingMutation || answeringCallId === call.id;
  const ending = isEnding || isEndingMutation;

  // prepare is the caller's own work before the answer (chat pickup, on-hold counter).
  // It runs after the claim below, so only the attempt that won it does that work: a
  // refused click must not pick the chat up or move the counter.
  const startCall = async (
    prepare?: () => Promise<void> | void
  ): Promise<boolean> => {
    if (starting) return false;
    // Read the live store, not this render's values: a second click (or the other
    // button) can arrive before this component re-renders. Every answer makes the
    // gateway build a new leg, so answering one call twice leaks a recording room.
    if (!selectCanAnswerCall(call.id)(store.getState())) return false;
    dispatch(answeringStarted(call.id));
    setIsStarting(true);

    // Hang up and Reject stay clickable while the answer is being set up. Once the
    // hang-up is confirmed the call is off screen, so an answer that keeps going would
    // leave the mic and a Janus leg live with no button left to end them.
    const hungUp = () => selectIsCallEnded(call.id)(store.getState());

    try {
      await prepare?.();
      const { sessionId, handleId } = await CallMediaSingleton.connect();

      if (!sessionId || !handleId) throw Error;

      // F004: generate roomId per startCall invocation so a redial gets a
      // fresh room instead of reusing the previous call's id.
      const roomId = generateRoomId();

      const payload: StartCallRequest = {
        chatId: call.chatId,
        agent: {
          name: agent.fullName,
          username: agent.localUserName || agent.email,
        },
        callContext: {
          call: {
            id: call.id,
            callee: call.callee,
            caller: call.caller,
            sdpOffer: call.session?.sdp as string,
          },
          sessionId,
          handleId,
          roomId,
        },
      };

      await CallMediaSingleton.joinRoom(roomId);
      if (hungUp()) {
        await CallMediaSingleton.leaveRoom();
        return false;
      }
      // unwrap: a rejected answer (e.g. 409, the call was already hung up) must fail
      // here, not pass as success with the agent's leg left alone in its room.
      await requestStartCallMutation(payload).unwrap();
      if (hungUp()) {
        await CallMediaSingleton.leaveRoom();
        return false;
      }
      // answeringCallId stays set until ONGOING or a terminal event clears it.
    } catch (error) {
      dispatch(answeringFinished(call.id));
      // Local teardown first: the error handler may destroy the Janus session, after
      // which a leave or detach would go nowhere.
      await CallMediaSingleton.leaveRoom();
      // The agent hung up meanwhile: the gateway's 409 is the expected outcome, not
      // an error to show.
      if (!hungUp()) handleCallMediaError(error);
      return false;
    } finally {
      setTimeout(() => setIsStarting(false), 1500);
    }

    return true;
  };

  const endCall = async (withMode?: boolean) => {
    if (ending) return;
    setIsEnding(true);

    const callAgent = (
      callDirection.isCallIncoming ? call.callee : call.caller
    ) as CallAgent;

    const contactUser = (
      callDirection.isCallIncoming ? call.caller : call.callee
    ) as CallUser;

    // leaveRoom() is null-safe: no-op if mic was never captured (e.g. incoming
    // call that was never answered). Unconditional call ensures the mic is
    // released locally even when hanging up in WAITING_ANSWER — without this
    // guard removal, no TERMINATED WebSocket event arrives and the mic stays on.
    CallMediaSingleton.leaveRoom();

    const data = {
      callId: call.id,
      chatId: call.chatId,
      whatsappPhoneNumberId: callAgent.whatsappPhoneNumberId,
      contactPhoneNumber: contactUser.phoneNumber,
      ...(contactUser.bsuid && { contactBsuid: contactUser.bsuid }),
      ...(withMode && { mode: 'RECOVERY' }),
    };

    try {
      await requestEndCallMutation(data).unwrap();
      // The hang-up is confirmed with Meta: end the call here rather than wait for
      // Meta's own terminate, which can take 15 s and leaves the call on screen as
      // active. The TERMINATED that arrives later is ignored for an ended call id.
      dispatch(endedCall(call.id));
    } catch {
      // Not confirmed: keep the call on screen so the agent can hang up again.
    } finally {
      setTimeout(() => setIsEnding(false), 1500);
    }
  };

  const muteCall = () => {
    setIsMuted(true);
    CallMediaSingleton.mute();
  };

  const unmuteCall = () => {
    setIsMuted(false);
    CallMediaSingleton.unmute();
  };

  return {
    startCall,
    endCall,
    muteCall,
    unmuteCall,
    muted: isMuted,
    starting,
    ending,
  };
};

export default useCallMedia;
